import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faMinus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { memo, useCallback } from 'react';

interface PlannerExpandButtonProps {
  siteId: string;
  expandCount: number;
  isRowExpanded: boolean;
  expandPillSize: number;
  jobPillMargin: number;
  jobPillHeight: number;
  transform: string;
  onToggle: (siteId: string) => void;
}

/**
 * PlannerExpandButton - Expand/collapse button for row overflow.
 * Memoized to prevent re-renders during scroll.
 */
export const PlannerExpandButton = memo(function PlannerExpandButton({
  siteId,
  expandCount,
  isRowExpanded,
  expandPillSize,
  jobPillMargin,
  jobPillHeight,
  transform,
  onToggle,
}: PlannerExpandButtonProps) {
  // useCallback keeps the handler stable to avoid re-renders in the memoized button
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onToggle(siteId);
    },
    [siteId, onToggle]
  );

  // useCallback keeps keyboard handling stable for memoization and predictable focus behavior
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onToggle(siteId);
    },
    [onToggle, siteId]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className={classNames(
        'flex select-none flex-col items-center justify-end text-xs'
      )}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: expandPillSize + jobPillMargin * 2,
        height: jobPillHeight,
        transform,
        zIndex: 10,
        touchAction: 'manipulation',
      }}
    >
      <button
        type='button'
        className='flex cursor-pointer select-none items-center justify-center whitespace-nowrap rounded-md border-0.5 border-outline-default bg-surface-container-high transition-colors duration-500 hover:bg-surface-container-low'
        aria-label={isRowExpanded ? 'Collapse row' : 'Expand row'}
        aria-expanded={isRowExpanded}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onTouchStart={handleTouchStart}
        tabIndex={0}
        style={{
          width: expandPillSize,
          height: expandPillSize,
          minWidth: '23px',
          minHeight: '23px',
          touchAction: 'manipulation',
          zIndex: 1,
        }}
      >
        {isRowExpanded ? (
          <FontAwesomeIcon icon={faMinus as IconProp} />
        ) : (
          '+' + (expandCount < 100 ? ' ' + expandCount : '')
        )}
      </button>
    </div>
  );
});

PlannerExpandButton.displayName = 'PlannerExpandButton';
